import React, { useState } from "react";
import { UserPlus } from "lucide-react";

export default function EmployeeAdding() {
  // Auto-generate new Employee ID
  const generateEmployeeId = () => {
    return `EMP${Date.now().toString().slice(-3)}`;
  };

  const [formData, setFormData] = useState({
    employeeName: "",
    employeeId: generateEmployeeId(),
    phone: "",
    address: "",
    role: "",
    department: "",
    joiningDate: "",
    status: "Active",
    salary: "",
    salaryType: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setFormData({
      employeeName: "",
      employeeId: generateEmployeeId(),
      phone: "",
      address: "",
      role: "",
      department: "",
      joiningDate: "",
      status: "Active",
      salary: "",
      salaryType: "",
    });
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const payload = {
      employee_id: formData.employeeId,
      name: formData.employeeName,
      phone: formData.phone,
      address: formData.address,
      role: formData.role,
      department: formData.department || undefined,
      joining_date: formData.joiningDate || undefined,
      status: formData.status,
      monthly_salary: formData.salary || undefined,
      salary_type: formData.salaryType || undefined,
    };

    try {
      const response = await fetch(
        "https://tailor.beezetsoftwaresolution.com/api/employee/add_employee.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      if (data.success) {
        setMessage(`✅ ${data.message}`);
        handleReset();
      } else {
        setMessage(`❌ Failed: ${data.message || "Unknown error"}`);
      }
    } catch (error) {
      setMessage("❌ Error connecting to server");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 p-4 sm:p-8">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg border border-blue-100 p-4 sm:p-6">
        <h2 className="text-2xl font-bold text-blue-800 mb-4 text-center">
          Employee Registration
        </h2>

        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/30">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-5 h-5 text-blue-700" />
            <h3 className="text-md font-semibold text-blue-900">
              Employee Information
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Employee Name *
                </label>
                <input
                  type="text"
                  name="employeeName"
                  value={formData.employeeName}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Employee ID (Auto-generated)
                </label>
                <input
                  type="text"
                  name="employeeId"
                  value={formData.employeeId}
                  readOnly
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 bg-gray-100 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Phone Number *
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="9876543210"
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-blue-900">
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter address"
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Role/Position *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Role</option>
                  <option value="Tailor">Tailor</option>
                  <option value="Designer">Designer</option>
                  <option value="Manager">Manager</option>
                  <option value="HR Manager">HR Manager</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Department
                </label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="Human Resources"
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Joining Date
                </label>
                <input
                  type="date"
                  name="joiningDate"
                  value={formData.joiningDate}
                  onChange={handleChange}
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Monthly Salary
                </label>
                <input
                  type="number"
                  name="salary"
                  value={formData.salary}
                  onChange={handleChange}
                  placeholder="65000"
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-900">
                  Salary Type
                </label>
                <select
                  name="salaryType"
                  value={formData.salaryType}
                  onChange={handleChange}
                  className="w-full mt-1 border border-blue-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Type</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Per Piece">Per Piece</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2 justify-center">
              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-2 rounded-md text-white ${
                  loading
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-700 hover:bg-blue-800"
                } transition`}
              >
                {loading ? "Saving..." : "Add Employee"}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="border border-blue-300 text-blue-700 px-6 py-2 rounded-md hover:bg-blue-100 transition"
              >
                Reset Form
              </button>
            </div>

            {message && (
              <p
                className={`text-center mt-4 font-medium ${
                  message.includes("✅")
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}